import React, { useEffect, useState } from 'react';
import { Film, ImageOff, Loader2, AlertTriangle, RefreshCw, LayoutGrid } from 'lucide-react';
import { resolveDownloadUrl } from '../../services/storageService';
import Tooltip from '../ui/Tooltip';
import { TYPE } from '../../constants/typeScale';
import type { StoryboardShot } from '../../services/studioService';
import { qualityMiss } from './qualityMiss';

/** A cast member the board knows about: the bible row, and whether their face is locked. */
export interface ScriptPerson { id: string; name: string; image_path: string | null }

interface Props {
  shots: StoryboardShot[];
  people: ScriptPerson[];
  /** Edit one shot's line in place (the same patch the board's node uses). */
  onEditLine: (index: number, line: string) => void;
  /** Draw (or redraw the changed) shot pictures. */
  onPreview: () => void;
  previewing: boolean;
  /** Draw ONE shot's picture again, charged like any picture (offered on a likeness miss). */
  onRedraw: (index: number) => void;
  /** The two film actions; absent until the episode can be filmed. */
  onQueueRender?: (quality: 'draft' | 'final') => void;
  queueing?: boolean;
  disabled?: boolean;
}

/** A shot's picture, resolved from its Storage path; a quiet placeholder until there is one. */
const ShotPicture: React.FC<{ path?: string | null; index: number; previewing: boolean }> = ({ path, index, previewing }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    setUrl(null); setFailed(false);
    if (path) resolveDownloadUrl(path).then((u) => { if (alive) setUrl(u); }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [path]);
  return (
    <div className="flex h-[199px] w-[112px] shrink-0 items-center justify-center overflow-hidden rounded-lg border border-secondary-200 bg-secondary-100 dark:border-secondary-800 dark:bg-secondary-800">
      {url ? <img src={url} alt={`Shot ${index + 1} sketch`} className="h-full w-full object-cover" />
        : failed ? <ImageOff className="h-4 w-4 text-secondary-400" aria-hidden />
          : path || previewing ? <Loader2 className="h-4 w-4 animate-spin text-secondary-400" aria-hidden />
            : <Film className="h-4 w-4 text-secondary-400" aria-hidden />}
    </div>
  );
};

/**
 * THE SHOTS PAGE AS A SCRIPT (UX-3, owner chose design 1c on 2026-10-21). A writer meets their episode as text they
 * can read and edit, each shot beside its picture, instead of as thumbnails on a zoomed-out canvas. The board stays
 * one click away (Arrange) for reordering and per-shot direction; this view is where the words are.
 */
const ShotScriptView: React.FC<Props> = ({ shots, people, onEditLine, onPreview, previewing, onRedraw, onQueueRender, queueing, disabled }) => {
  const nameOf = (id: string) => people.find((p) => p.id === id);
  const anyPicture = shots.some((s) => s.previewFramePath);
  return (
    <div className="ms-page mx-auto w-full max-w-[760px]" data-testid="shot-script">
      <div className="flex flex-wrap items-center gap-2 border-b border-secondary-200 pb-3 dark:border-secondary-800">
        <p className={`mr-auto ${TYPE.body} text-secondary-500 dark:text-secondary-400`}>
          Read your episode shot by shot. Each picture is a quick sketch of the shot, made before the film.
        </p>
        <button type="button" onClick={onPreview} disabled={previewing || disabled} data-testid="script-preview"
          className={`inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 px-3 py-1.5 ${TYPE.control} font-semibold text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 dark:border-secondary-700 dark:text-secondary-200 dark:hover:bg-secondary-800`}>
          {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <LayoutGrid className="h-3.5 w-3.5" aria-hidden />}
          {previewing ? 'Drawing…' : anyPicture ? 'Redraw changed shots' : 'Draw the shots'}
        </button>
        {onQueueRender && (<>
          <Tooltip content="A quick, low-cost pass so you can see whether these shots work. Not for publishing.">
            <button type="button" onClick={() => onQueueRender('draft')} disabled={queueing || !shots.length} data-testid="script-queue-draft"
              className={`rounded-lg border border-secondary-300 px-3 py-1.5 ${TYPE.control} font-semibold text-secondary-700 disabled:opacity-40 dark:border-secondary-600 dark:text-secondary-200`}>
              Quick rough cut
            </button>
          </Tooltip>
          <Tooltip content="The finished film at full quality, the only version you can put on your store.">
            <button type="button" onClick={() => onQueueRender('final')} disabled={queueing || !shots.length} data-testid="script-queue-render"
              className={`rounded-lg bg-secondary-900 px-3 py-1.5 ${TYPE.control} font-semibold text-white disabled:opacity-40 dark:bg-white dark:text-secondary-900`}>
              {queueing ? 'Making the film…' : 'Make the film'}
            </button>
          </Tooltip>
        </>)}
      </div>

      <ol>
        {shots.map((shot, i) => {
          const miss = qualityMiss(shot);
          const cast = (shot.character_ids || []).map(nameOf).filter((p): p is ScriptPerson => !!p);
          return (
            <li key={i} className="flex gap-4 border-b border-secondary-200 py-4 dark:border-secondary-800" data-testid={`script-shot-${i + 1}`}>
              <ShotPicture path={shot.previewFramePath} index={i} previewing={previewing} />
              <div className="min-w-0 flex-1">
                <div className={`mb-1.5 flex flex-wrap items-center gap-2 ${TYPE.meta} text-secondary-500 dark:text-secondary-400`}>
                  <span className="font-semibold">Shot {i + 1}</span>
                  {cast.map((p) => (
                    <span key={p.id} className="rounded-full border border-secondary-300 px-2 py-0.5 dark:border-secondary-700">
                      {p.name}{p.image_path ? ', face locked' : ''}
                    </span>
                  ))}
                </div>
                <textarea
                  value={shot.line || ''}
                  onChange={(e) => onEditLine(i, e.target.value)}
                  aria-label={`Shot ${i + 1}`}
                  rows={Math.max(2, Math.ceil((shot.line || '').length / 70))}
                  placeholder="What happens in this shot"
                  className={`block w-full resize-none border-0 bg-transparent p-0 ${TYPE.manuscript} text-secondary-900 outline-none placeholder:text-secondary-400 dark:text-secondary-100`}
                />
                {(shot.dialogue || []).filter((d) => d.line).map((d, j) => (
                  <p key={j} className={`mt-1 ${TYPE.dialogue} text-secondary-700 dark:text-secondary-300`}>
                    {d.character && <span className={`mr-2 not-italic ${TYPE.label} font-sans text-secondary-500`}>{d.character}</span>}
                    {d.line}
                  </p>
                ))}
                {miss && !previewing && (
                  <div className={`mt-2 flex flex-wrap items-center gap-2 ${TYPE.meta}`} data-testid={`script-quality-miss-${i + 1}`}>
                    <Tooltip content={miss.detail}>
                      <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> {miss.label}
                      </span>
                    </Tooltip>
                    <button type="button" onClick={() => onRedraw(i)} disabled={disabled} aria-label={`Redraw shot ${i + 1}`}
                      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-secondary-600 hover:bg-secondary-100 disabled:opacity-50 dark:text-secondary-300 dark:hover:bg-secondary-800">
                      <RefreshCw className="h-3 w-3" aria-hidden /> Redraw
                    </button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default ShotScriptView;
